import React, { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Typography } from "../ui/typography";
import ConfirmationModal from "@/components/common/ConfirmationModal";
import { useToast } from "@/hooks/use-toast";

// Assets
import RestoreLayoutArrow from "@/assets/svg/RestoreLayoutArrow.svg";
import DefaultLayoutArrowLight from "@/assets/svg/DefaultLayoutArrowLight.svg";
import PreferredLayoutArrowLight from "@/assets/svg/PreferredLayoutArrowLight.svg";
import ModalDefaultLayoutArrowLight from "@/assets/svg/ModalDefaultArrowLight.svg";
import ModalPreferredLayoutLight from "@/assets/svg/ModalPreferredLight.svg";
import ToasterLight from "@/assets/svg/ToasterLight.svg";
import { MESSAGES } from "@/constants/messages";

export interface RestoreLayoutDialogBoxProps {
  /** Show the Default Layout option (hide when user is already on default and preferred exists). */
  showDefault?: boolean;
  /** Show the Preferred Layout option (only when preferred layout is saved and user is on default). */
  showPreferred?: boolean;
  onRestore?: (type: "default" | "preferred") => Promise<void>;
  isRestoring?: boolean;
  /** Disables the entire Restore Layout button — used when the user has no preferred layout saved yet. */
  disabled?: boolean;
}

export const RestoreLayoutDialogBox: React.FC<RestoreLayoutDialogBoxProps> = ({
  showDefault = true,
  showPreferred = false,
  onRestore,
  isRestoring = false,
  disabled = false,
}) => {
  const { toast } = useToast();
  const [isDefaultLayoutModalOpen, setIsDefaultLayoutModalOpen] = useState(false);
  const [isPreferredLayoutModalOpen, setIsPreferredLayoutModalOpen] = useState(false);
  const [internalLoadingDefault, setInternalLoadingDefault] = useState(false);
  const [internalLoadingPreferred, setInternalLoadingPreferred] = useState(false);

  const handleRestoreDefault = async () => {
    if (!onRestore) return;
    setInternalLoadingDefault(true);
    try {
      await onRestore("default");
      setIsDefaultLayoutModalOpen(false);
      toast({
        description: (
          <div className="flex items-center gap-3">
            <img src={ToasterLight} alt="success" className="w-6 h-6 flex-shrink-0" />
            <Typography variant="medium-16" className="text-grey-800 dark:text-white whitespace-nowrap">
              {MESSAGES.defaultLayoutRestored}
            </Typography>
          </div>
        ),
        duration: 2500,
      });
    } catch (error) {
      console.error("Failed to restore default layout:", error);
    } finally {
      setInternalLoadingDefault(false);
    }
  };

  const handleRestorePreferred = async () => {
    if (!onRestore) return;
    setInternalLoadingPreferred(true);
    try {
      await onRestore("preferred");
      setIsPreferredLayoutModalOpen(false);
      toast({
        description: (
          <div className="flex items-center gap-3">
            <img src={ToasterLight} alt="success" className="w-6 h-6 flex-shrink-0" />
            <Typography variant="medium-16" className="text-grey-800 dark:text-white whitespace-nowrap">
              {MESSAGES.preferredLayoutRestored}
            </Typography>
          </div>
        ),
        duration: 2500,
      });
    } catch (error) {
      console.error("Failed to restore preferred layout:", error);
    } finally {
      setInternalLoadingPreferred(false);
    }
  };

  const isDisabled = isRestoring || disabled;

  // Blue-500 (#3b82f6) themed not-allowed SVG cursor with fallback to native not-allowed.
  // The wrapper span owns pointer events when the button is disabled so the cursor
  // is visible immediately on hover — pointer-events:none on the button itself
  // blocks actual clicks without hiding the cursor from the browser.
  const disabledCursorStyle: React.CSSProperties = {
    cursor: [
      "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20'%3E%3Ccircle cx='10' cy='10' r='8' fill='none' stroke='%236b7280' stroke-width='2.5'/%3E%3Cline x1='3.64' y1='3.64' x2='16.36' y2='16.36' stroke='%236b7280' stroke-width='2.5'/%3E%3C/svg%3E\") 10 10",
      "not-allowed",
    ].join(", "),
  };

  return (
    <>
      {/* Wrapper absorbs hover so the not-allowed cursor is visible even though the
          inner button has pointer-events:none. It must not be a button/interactive
          element itself to preserve keyboard navigation on the real button. */}
      <span
        className="inline-flex mr-2"
        style={isDisabled ? disabledCursorStyle : undefined}
        aria-hidden={isDisabled ? true : undefined}
      >
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={isDisabled}>
          <button
            className={`flex items-center gap-2 outline-none bg-transparent border-none px-0 h-[32px] rounded-md focus:outline-none ${isDisabled ? "opacity-50 pointer-events-none" : "cursor-pointer"}`}
            disabled={isDisabled}
            aria-disabled={isDisabled}
          >
            <Typography variant="medium-14" className="text-primary-600 dark:text-primary-400 whitespace-nowrap">
              Restore Layout
            </Typography>
            <img src={RestoreLayoutArrow} alt="restore layout" className="w-[18px] h-[18px]" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="center"
          className="min-w-[180px] bg-white dark:bg-blue-900 border border-grey-300 dark:border-grey-600 rounded drop-shadow-md z-50 p-2 overflow-hidden"
        >
          {showDefault && (
            <DropdownMenuItem
              className="flex flex-1 items-center gap-3 cursor-pointer focus:bg-gray-50 focus:text-grey-800 dark:focus:bg-blue-800 h-[40px] px-3 rounded-md outline-none"
              onClick={() => setIsDefaultLayoutModalOpen(true)}
            >
              <img src={DefaultLayoutArrowLight} alt="default layout" className="w-[16px] h-[16px] dark:invert flex-shrink-0" />
              <Typography variant="medium-14" className="text-grey-800 dark:text-grey-100 whitespace-nowrap">
                Default Layout
              </Typography>
            </DropdownMenuItem>
          )}
          {showDefault && showPreferred && (
            <DropdownMenuSeparator className="bg-grey-200 dark:bg-grey-700 my-1 mx-1" />
          )}
          {showPreferred && (
            <DropdownMenuItem
              className="flex flex-1 items-center gap-3 cursor-pointer focus:bg-gray-50 focus:text-grey-800 dark:focus:bg-blue-800 h-[40px] px-3 rounded-md outline-none"
              onClick={() => setIsPreferredLayoutModalOpen(true)}
            >
              <img src={PreferredLayoutArrowLight} alt="my layout" className="w-[16px] h-[16px] dark:invert flex-shrink-0" />
              <Typography variant="medium-14" className="text-grey-800 dark:text-grey-100 whitespace-nowrap">
                My Layout
              </Typography>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      </span>

      <ConfirmationModal
        isOpen={isDefaultLayoutModalOpen}
        onClose={() => !internalLoadingDefault && setIsDefaultLayoutModalOpen(false)}
        title="Restore Default Layout"
        description="Do you want to Restore Default Layout ?"
        subText={"This will replace your current layout with the default dashboard layout.\nYou can switch back to My Layout anytime from the 'Edit Layout' mode."}
        modalWidth="583px"
        buttons={[
          {
            label: "Cancel",
            onClick: () => setIsDefaultLayoutModalOpen(false),
            variant: "outline",
            className: internalLoadingDefault ? "pointer-events-none opacity-50" : "",
          },
          {
            label: "Restore Default Layout",
            onClick: handleRestoreDefault,
            variant: "contained1",
            icon: ModalDefaultLayoutArrowLight,
            isLoading: internalLoadingDefault,
          },
        ]}
      />

      <ConfirmationModal
        isOpen={isPreferredLayoutModalOpen}
        onClose={() => !internalLoadingPreferred && setIsPreferredLayoutModalOpen(false)}
        title="Restore My Layout"
        description="Do you want to Restore My Layout ?"
        subText="This will replace the current Default Layout to your saved My Layout."
        modalWidth="583px"
        buttons={[
          {
            label: "Cancel",
            onClick: () => setIsPreferredLayoutModalOpen(false),
            variant: "outline",
            className: internalLoadingPreferred ? "pointer-events-none opacity-50" : "",
          },
          {
            label: "Restore My Layout",
            onClick: handleRestorePreferred,
            variant: "contained1",
            icon: ModalPreferredLayoutLight,
            isLoading: internalLoadingPreferred,
          },
        ]}
      />
    </>
  );
};

export default RestoreLayoutDialogBox;
